import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  CheckCircle2,
  Loader2,
  Calendar,
  Clock,
  Video,
  Key,
  ArrowRight,
  Download,
  Share2,
  XCircle,
  AlertCircle
} from 'lucide-react';

const WebinarPaymentSuccess = () => {
  const { webinarId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [accessInfo, setAccessInfo] = useState(null);
  const [webinar, setWebinar] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('No se encontró información del pago');
      setLoading(false);
      return;
    }

    confirmPayment(sessionId);
  }, [searchParams, webinarId]);

  const confirmPayment = async (sessionId) => {
    try {
      // Confirm payment
      const response = await api.post(`/webinars/${webinarId}/confirm-payment?session_id=${sessionId}`);
      
      setSuccess(true);
      setAccessInfo(response.data);
      
      // Get webinar details
      const webinarRes = await api.get(`/webinars/${webinarId}`);
      setWebinar(webinarRes.data);
      
      toast({
        title: "¡Pago exitoso!",
        description: "Ya tienes acceso al webinar"
      });
      
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError(error.response?.data?.detail || 'Error al confirmar el pago');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Confirmando tu pago...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Error en el pago
              </h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate(`/webinars/${webinarId}`)}
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  Intentar de nuevo
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contacto')}
                >
                  Contactar soporte
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0 overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 p-8 text-center text-white">
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <h1 className="text-3xl font-bold mb-2">¡Pago Exitoso!</h1>
              <p className="text-green-100">Ya tienes acceso al webinar</p>
            </div>
            
            <CardContent className="p-8">
              {/* Webinar Info */}
              {webinar && (
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h2 className="font-bold text-xl text-gray-900 mb-3">{webinar.title}</h2>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(webinar.date)}
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Duración: {webinar.duration_minutes} minutos
                    </div>
                    <div className="flex items-center">
                      <Video className="h-4 w-4 mr-2" />
                      Plataforma: {webinar.platform}
                    </div>
                  </div>
                </div>
              )}

              {/* Access Token */}
              {accessInfo?.access_token && (
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 mb-6 text-center">
                  <div className="flex items-center justify-center mb-3">
                    <Key className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Tu Token de Acceso Único</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 font-mono text-sm break-all text-gray-800">
                    {accessInfo.access_token}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Este token es personal e intransferible. No lo compartas.
                  </p>
                </div>
              )}

              {/* Important Notice */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">Importante:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>El enlace de acceso es único para ti</li>
                      <li>Solo podrás acceder desde un dispositivo</li>
                      <li>Te enviaremos recordatorios por email</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="flex-1 bg-gray-900 hover:bg-gray-800 h-12"
                  onClick={() => navigate(`/webinars/${webinarId}/sala`)}
                >
                  Ir a la Sala de Espera
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-12"
                  onClick={() => navigate('/dashboard')}
                >
                  Ver mi Dashboard
                </Button>
              </div>

              {/* Footer */}
              <p className="text-center text-sm text-gray-500 mt-6">
                Se ha enviado un recibo a tu correo electrónico
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default WebinarPaymentSuccess;
